/**
 * `CustomSkillModal` — ADR-0162 §3 Slice 4 + §3c.
 *
 * The dashboard front for `POST /v1/skills` (create) — merchant defines a
 * custom skill that reaches the signed agent card + MCP `tools/list`.
 * Three fulfillment modes: `discovery` (advertised-but-manual), `webhook`
 * (Sill proxies the agent's `tools/call` to a merchant-owned HTTPS
 * endpoint on the site's verified domain), and `mcp_passthrough` (Sill
 * relays the call to the merchant's own MCP server as a JSON-RPC
 * `tools/call`). `webhook` and `mcp_passthrough` are UI-symmetric — both
 * require an endpoint + mint a shared secret shown-once — because both
 * ride the identical origin dispatch (SSRF-guarded, HMAC-signed,
 * gate-8-sanitized); only the outbound body framing differs.
 *
 * Ported from `AddSkillModal` (onboarding, `Foyer_Dashboard_CURRENT.jsx`
 * line 1367): two-pane layout — form on the left, live agent-card
 * preview on the right. Uses the same `Modal` / `Field` / `Toggle`
 * atoms + raw-input styling; no new design tokens.
 *
 * Deviations from the onboarding modal (deliberate; kept minimal):
 *   - Adds a fulfillment-mode segmented control (three options).
 *   - Adds a conditional `fulfillment_endpoint` input for the two
 *     origin-dispatched kinds.
 *   - Adds a shown-once secret panel (`SecretShownOnce`) that replaces
 *     the form after a webhook OR mcp_passthrough create succeeds. The
 *     secret is the ONLY time it is retrievable — the origin never
 *     returns it again.
 *   - Adds inline server-error mapping (`ApiError.server_reason` →
 *     merchant-facing copy).
 *   - Drops the `auth_required` toggle (custom skills default false;
 *     Phase-1 discovery advertising has no mandate — ADR-0162 §2.1).
 *   - Adds the r18 informational line (ADR-0162 §2.5).
 *
 * The client-side validation on `fulfillment_endpoint` is a
 * pre-submit UX guard (HTTPS + non-empty); the server is the authority
 * on domain match (verified domain / SSRF layer) and surfaces the
 * specific reason via `fulfillment_endpoint_invalid`.
 */

import { useState } from 'react';

import type { Skill, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useC } from '../../lib/theme.js';
import { Card, Field, Modal } from '../../components/atoms/index.js';
import { SecretShownOnce } from './SecretShownOnce.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type FulfillmentKind = 'discovery' | 'webhook' | 'mcp_passthrough';

/** ADR-0162 §3c — the two origin-dispatched kinds share the identical
 *  management surface (endpoint required, shared secret minted + shown
 *  once). This predicate collapses the UI branch. */
function requiresEndpoint(kind: FulfillmentKind): boolean {
  return kind === 'webhook' || kind === 'mcp_passthrough';
}

export interface CustomSkillModalProps {
  open: boolean;
  onClose: () => void;
  site_id: SiteId;
  onCreated: (skill: Skill) => void;
}

// Skill-id regex is enforced server-side (`^[a-z][a-z0-9_]*$`, ADR-0162
// §2.2). Mirror it here for inline validation so a merchant sees the
// error at the boundary rather than round-tripping.
const SKILL_ID_RE = /^[a-z][a-z0-9_]*$/;

/**
 * Map an `ApiError.server_reason` (or an HTTP-status fallback) to a
 * merchant-facing inline message. Keeping this mapping in one place
 * so the copy is consistent with SettingsSkills' Edit / Rotate flows.
 */
function serverReasonCopy(err: ApiError): string {
  if (err.kind === 'rate_limited') {
    return 'Too many changes — try again in a moment.';
  }
  switch (err.server_reason) {
    case 'skill_exists':
      return 'A skill with that id already exists.';
    case 'skill_id_reserved':
      return 'That id is reserved — pick a different one.';
    case 'fulfillment_endpoint_invalid':
      return 'Endpoint must be HTTPS on your verified domain.';
    case 'role_insufficient':
      return 'Only owners and admins can add skills.';
    case 'webhook_fulfillment_unavailable':
      return "Webhook fulfillment isn't available on this deployment.";
    default:
      break;
  }
  // Fallback: derive from the kind. Keeps a stray 4xx honest instead
  // of surfacing a raw `bad_request:400` string.
  if (err.kind === 'bad_request') return 'Check the fields and try again.';
  if (err.kind === 'forbidden' || err.kind === 'not_found') {
    return 'You do not have permission to add a skill on this site.';
  }
  if (err.kind === 'service_unavailable') {
    return 'The service is temporarily unavailable. Try again in a moment.';
  }
  return 'Could not save the skill. Try again.';
}

export function CustomSkillModal({
  open,
  onClose,
  site_id,
  onCreated,
}: CustomSkillModalProps): JSX.Element | null {
  const C = useC();
  const api = useApi();

  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [desc, setDesc] = useState('');
  const [kind, setKind] = useState<FulfillmentKind>('discovery');
  const [endpoint, setEndpoint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // The shown-once secret + the just-created skill. When present, the
  // modal renders `SecretShownOnce` instead of the form. Dismissing
  // that panel is what fires `onCreated` + `onClose` for a webhook.
  const [minted, setMinted] = useState<
    { secret: string; skill: Skill } | null
  >(null);

  const generatedId =
    id || (name ? name.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40) : '');

  const idValid = generatedId.length > 0 && SKILL_ID_RE.test(generatedId);
  const nameValid = name.trim().length > 0;
  const descValid = desc.trim().length > 0;
  const endpointValid =
    !requiresEndpoint(kind) ||
    (endpoint.trim().length > 0 && /^https:\/\//i.test(endpoint.trim()));
  const canSubmit =
    nameValid && idValid && descValid && endpointValid && !submitting;
  // Dirty guard — any user-authored content means Escape prompts
  // "Discard changes?" instead of silently dropping the draft.
  const dirty =
    name.length > 0
    || id.length > 0
    || desc.length > 0
    || endpoint.length > 0
    || kind !== 'discovery';

  const reset = (): void => {
    setName('');
    setId('');
    setDesc('');
    setKind('discovery');
    setEndpoint('');
    setErrorMsg(null);
    setSubmitting(false);
    setMinted(null);
  };

  const closeAndReset = (): void => {
    reset();
    onClose();
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await api.skills.createCustom(site_id, {
        skill_id: generatedId,
        name: name.trim(),
        description: desc.trim(),
        fulfillment_kind: kind,
        ...(requiresEndpoint(kind)
          ? { fulfillment_endpoint: endpoint.trim() }
          : {}),
      });
      // ADR-0162 §3c — both origin-dispatched kinds carry a shown-once
      // secret; discovery does not. The check is on `webhook_secret`
      // presence + the kind being endpoint-requiring so a future kind
      // that omits the secret doesn't trap the merchant on this panel.
      if (requiresEndpoint(kind) && typeof res.webhook_secret === 'string') {
        // Show the shown-once secret before firing `onCreated` — the
        // merchant must have a chance to copy it. Dismissal of the
        // secret panel is what commits + closes.
        setMinted({ secret: res.webhook_secret, skill: res.skill });
      } else {
        onCreated(res.skill);
        closeAndReset();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMsg(serverReasonCopy(err));
      } else {
        setErrorMsg('Could not save the skill. Try again.');
      }
      setSubmitting(false);
    }
  };

  // Post-webhook success path — the shown-once secret panel replaces
  // the form. `onDismiss` fires `onCreated` (parent refetches +
  // appends) and closes the modal.
  if (open && minted !== null) {
    return (
      <Modal
        open={open}
        onClose={() => {
          onCreated(minted.skill);
          closeAndReset();
        }}
        size="md"
        title="Copy your webhook secret"
        subtitle="Shown once. Sill never displays it again."
      >
        <div className="p-6 space-y-4">
          <SecretShownOnce secret={minted.secret} />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                onCreated(minted.skill);
                closeAndReset();
              }}
              className="flex-1 sm:flex-none px-6 py-2.5"
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
              }}
            >
              I have copied the secret
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={closeAndReset}
      size="lg"
      title="Add custom skill"
      subtitle="Becomes a callable A2A capability on your agent card."
      closeOnBackdrop={false}
      dirty={dirty}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="p-6 space-y-4">
          <Field label="Display name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Schedule a tasting"
              className="w-full px-3 py-2.5 outline-none"
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                color: C.text,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            />
          </Field>
          <Field
            label="Skill ID"
            hint="Lowercase, underscores. Used by agents to call this skill."
          >
            <input
              value={generatedId}
              onChange={(e) =>
                setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
              }
              placeholder="schedule_tasting"
              className="w-full px-3 py-2.5 outline-none"
              style={{
                fontFamily: monoFont,
                fontSize: 13,
                color: C.text,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            />
          </Field>
          <Field
            label="Description"
            hint="One sentence. Agents pattern-match on this text."
            required
          >
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={3}
              placeholder="What does this skill do? Be specific."
              className="w-full px-3 py-2.5 outline-none resize-none"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            />
          </Field>

          <Field
            label="Fulfillment"
            hint="Advertised = you fulfill manually. Webhook = Sill POSTs a Sill body. MCP = Sill relays as a JSON-RPC tools/call."
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <FulfillmentOption
                selected={kind === 'discovery'}
                onClick={() => setKind('discovery')}
                title="Advertised"
                body="You fulfill manually — Sill lists the skill on your agent card."
              />
              <FulfillmentOption
                selected={kind === 'webhook'}
                onClick={() => setKind('webhook')}
                title="Live webhook"
                body="Sill proxies the agent's call to your HTTPS endpoint."
              />
              <FulfillmentOption
                selected={kind === 'mcp_passthrough'}
                onClick={() => setKind('mcp_passthrough')}
                title="MCP server"
                body="Sill relays as an MCP tools/call to your MCP server."
              />
            </div>
          </Field>

          {requiresEndpoint(kind) ? (
            <Field
              label="Fulfillment endpoint"
              hint={
                kind === 'mcp_passthrough'
                  ? "Your MCP server's Streamable-HTTP URL on your verified domain."
                  : 'Must be an HTTPS URL on your verified domain.'
              }
              required
            >
              <input
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder={
                  kind === 'mcp_passthrough'
                    ? 'https://mcp.your-site.com/'
                    : 'https://hooks.your-site.com/skills/schedule_tasting'
                }
                className="w-full px-3 py-2.5 outline-none"
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: C.text,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                }}
              />
            </Field>
          ) : null}

          {/* ADR-0162 §2.5 — r18 note. Informational, not blocking. */}
          <div
            className="p-3"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                marginBottom: 6,
              }}
            >
              WHAT HAPPENS ON SAVE
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
              }}
            >
              Adding a skill updates your signed skill manifest. Agents using
              transactional mandates will re-verify on their next call.
            </div>
          </div>

          {errorMsg !== null ? (
            <div
              role="alert"
              data-testid="custom-skill-error"
              className="p-3"
              style={{
                background: C.coralDim,
                border: `1px solid rgba(255,107,71,0.3)`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
              }}
            >
              {errorMsg}
            </div>
          ) : null}
        </div>

        <div
          className="p-6 lg:border-l"
          style={{ background: C.surface2, borderColor: C.border }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 16,
            }}
          >
            AGENT CARD PREVIEW
          </div>
          <Card className="p-4">
            <pre
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                lineHeight: 1.7,
                color: C.muted,
                whiteSpace: 'pre-wrap',
                margin: 0,
              }}
            >
              {`{
  "id": "${generatedId || 'your_skill_id'}",
  "name": "${name || 'Your skill name'}",
  "description": "${desc || 'Skill description...'}",
  "fulfillment_kind": "${kind}"${
    requiresEndpoint(kind)
      ? `,
  "fulfillment_endpoint": "${endpoint || 'https://…'}"`
      : ''
  }
}`}
            </pre>
          </Card>
        </div>
      </div>

      <div
        className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 px-6 py-4"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <button
          type="button"
          onClick={closeAndReset}
          disabled={submitting}
          className="px-4 py-2.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            void submit();
          }}
          disabled={!canSubmit}
          data-testid="custom-skill-submit"
          className="flex-1 sm:flex-none px-6 py-2.5"
          style={{
            background: canSubmit ? C.lime : C.surface2,
            color: canSubmit ? C.ink : C.muted,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            opacity: canSubmit ? 1 : 0.6,
          }}
        >
          {submitting ? 'Saving…' : 'Add skill'}
        </button>
      </div>
    </Modal>
  );
}

interface FulfillmentOptionProps {
  selected: boolean;
  onClick: () => void;
  title: string;
  body: string;
}

/**
 * Two-option segmented control for the fulfillment mode. Mirrors the
 * re-scan-schedule button-group density in `SettingsEndpoint.tsx`
 * (lime border on selected, plain border otherwise; 2px radius).
 */
function FulfillmentOption({
  selected,
  onClick,
  title,
  body,
}: FulfillmentOptionProps): JSX.Element {
  const C = useC();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className="text-left p-3"
      style={{
        background: selected ? C.surface2 : C.surface,
        border: `1px solid ${selected ? C.lime : C.border}`,
        borderRadius: 2,
      }}
    >
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 500,
          color: C.text,
          marginBottom: 2,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {body}
      </div>
    </button>
  );
}
